import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useAlert } from 'react-alert'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { RootState } from '../../../../states/containers/store'
import { createTaskMemberCosts, fetchTaskMemberCosts, updateTaskMemberCosts } from '../../../../states/containers/task/taskSlice'
import LoadingSquare from '../../../components/common/LoadingSquare'
import CostModal from '../../../components/task/task_cost/CostModal'
import ImportMemberCostModal from '../../../components/task/task_cost/ImportMemberCostModal'
import MemberCostTable from '../../../components/task/task_cost/MemberCostTable'
import { compile } from '../../../../utils/search'
import { TaskMemberCost, TaskMemberCostsMember } from '../../../../states/types/task'

type Member = TaskMemberCostsMember
type MemberCost = Omit<TaskMemberCost, 'id'>

interface UpdateMemberCosts {
  member_id: number
  member_costs: {
    hourly_pay: number
    period_start: string | null
    period_end: string | null
  }[]
}

interface ModalState {
  id: number
  name: string
  member_costs: MemberCost[]
}

interface List {
  members: Member[]
}

const DISPLAY_BLOCK_NUMBER = 20

const INIT_MODAL_STATE: ModalState = {
  id: 0,
  name: "",
  member_costs: [],
}

const Index: FC = () => {
  const {
    login,
    task,
  } = useSelector((state: RootState) => state)

  const location = useLocation()
  const dispatch = useDispatch()
  const alert = useAlert()

  const [composition, setComposition] = useState<boolean>(false)
  const [searchWord, setSearchWord] = useState<string>("")
  const [data, setData] = useState<List>({ members: [] })
  const [dataInit, setDataInit] = useState<List>({ members: [] })

  const [isDisplayMember, setIsDisplayMember] = useState<boolean>(false)

  const [modalState, setModalState] = useState<ModalState>(INIT_MODAL_STATE)

  const [isOpenImportModal, setIsOpenImportModal] = useState<boolean>(false)

  const [displayBlockNum, setDisplayBlockNum] = useState<number>(1)

  const paginatedMembers = useMemo(
    () => data.members.slice(0, displayBlockNum * DISPLAY_BLOCK_NUMBER),
    [data.members, displayBlockNum],
  )

  const currentScroll = () => Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop)
  const bottomHeight = () => document.getElementsByClassName("main")[0].clientHeight
  const windowHeight = () => document.documentElement.clientHeight

  const onScroll = useCallback(() => {
    const adjustment = window.innerWidth >= 1280 ? 0.90 : 0.85
    if (
      (bottomHeight() * adjustment) < (currentScroll() + windowHeight())
      && data.members.length >= displayBlockNum * DISPLAY_BLOCK_NUMBER
    ) {
      setDisplayBlockNum((self) => self + 1)
    }
  }, [displayBlockNum, data])

  const keyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !composition) {
      event.currentTarget.blur()
    }
  }

  const reset = () => {
    setSearchWord("")
    setData({ members: dataInit.members })
  }

  const search = (originalList: Member[] | undefined) => {
    if (!searchWord) {
      reset()
      setDisplayBlockNum(1)
      return
    }
    const word = searchWord.toLowerCase().split(/\s+/)
    const list = originalList?.filter((v) => {
      if (v === null) return false
      return (
        compile(word).test(v.name.toLowerCase() + v.kana?.toLowerCase() + v.code?.toLowerCase())
      )
    }) as Member[]
    setData({ members: list })
    setDisplayBlockNum(1)
  }

  const modCost = (id: number, cost: MemberCost[]) => {
    const member = data.members.find((v) => v.id === id)
    dispatch(
      updateTaskMemberCosts(
        {
          id: id,
          member_costs: cost,
        },
        () => {
          alert.success(`${member?.name ?? ''}さんの1時間あたりのコストを変更しました。`)
          setIsDisplayMember(false)
        },
      ),
    )
  }

  const modCosts = (costData: UpdateMemberCosts[]) => {
    dispatch(
      createTaskMemberCosts(
        { entries: costData },
        () => {
          alert.success(`ユーザーコストを一括登録しました。`)
          setIsDisplayMember(false)
        },
      ),
    )
  }

  const openMemberModal = useCallback((member: Member) => {
    setModalState({
      id: member.id,
      name: member.name,
      member_costs: member.member_costs,
    })
    setIsDisplayMember(true)
  }, [])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location])

  useEffect(() => {
    if (!login.accessToken) return
    dispatch(fetchTaskMemberCosts())
  }, [login.accessToken])

  useEffect(() => {
    const members = task.taskMemberCosts?.members ?? []
    setDataInit({ members })
    if (!searchWord) {
      setData({ members })
    }
  }, [task.taskMemberCosts])

  useEffect(() => {
    document.addEventListener("scroll", onScroll)
    return () => {
      document.removeEventListener("scroll", onScroll)
    }
  }, [onScroll])

  return (
    <section className="task_page">
      <Helmet>
        <title>Qasee - 工数管理 - ユーザーコスト設定</title>
      </Helmet>
      <section className="breadcrumb">
        工数管理
        &nbsp;&gt;&nbsp;
        ユーザーコスト設定
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          ユーザーコスト設定
        </h1>
        <div className="tools">
          <div className="button_area">
            <button
              type="button"
              className="button"
              onClick={() => setIsOpenImportModal(true)}
            >
              <FontAwesomeIcon icon={['fas', 'plus']} />
              &nbsp;
              ユーザーコストを一括登録する
            </button>
          </div>
        </div>
      </section>
      <section className="page_description">
        <p>
          各ユーザーの1時間あたりのコストを登録・変更できます。
        </p>
        <p>
          設定するとユーザーの業務時間に応じて、各プロジェクトにかかったコストを自動計算します。
        </p>
        <p>
          ※この設定は任意項目です。
        </p>
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-header">
            <div className="title">
              ユーザーの個別設定
            </div>
            <div className="search_area">
              <input
                type="text"
                placeholder="名前・コードでユーザー検索"
                onChange={(event) => setSearchWord(event.target.value)}
                onBlur={() => search(dataInit.members)}
                onKeyDown={(event) => keyDown(event)}
                onCompositionStart={() => setComposition(true)}
                onCompositionEnd={() => setComposition(false)}
                value={searchWord}
                style={{ width: "300px" }}
              />
              {
                searchWord && (
                  <button type="button" className="reset" onClick={reset}>
                    <FontAwesomeIcon icon={['far', 'times-circle']} />
                  </button>
                )
              }
            </div>
          </div>
          <div className="block-detail">
            {
              task.isLoading ? (
                <LoadingSquare>
                  <>
                    ユーザーコスト設定を読み込んでいます…
                  </>
                </LoadingSquare>
              ) : (
                <MemberCostTable
                  members={paginatedMembers}
                  openMemberModalCallback={openMemberModal}
                />
              )
            }
          </div>
        </div>
      </section>
      <CostModal
        modalState={modalState}
        closeCallback={() => setIsDisplayMember(false)}
        modCallback={(id, modified) => modCost(id, modified)}
        isDisplay={isDisplayMember}
      />
      <ImportMemberCostModal
        isDisplay={isOpenImportModal}
        memberList={data.members}
        closeCallback={() => setIsOpenImportModal(false)}
        changeCallback={(costData) => modCosts(costData)}
      />
    </section>
  )
}

export default Index
